import { useEffect, useState } from 'react';
import { Box, Typography, Button, Paper, Alert, CircularProgress, Chip } from '@mui/material';
import axios from '../../api/axiosInstance';
import { useUser } from '../../context/userContext';

const PP = "'Poppins', sans-serif";

const STATUS_LABELS = {
  PendingReview: { label: 'Pending Review', color: '#92400E', bg: '#FEF3C7', desc: 'Your application has been received and is queued for the accountant to review and set your commission rates.' },
  PendingApproval: { label: 'Pending Final Approval', color: '#1E40AF', bg: '#DBEAFE', desc: 'The accountant has reviewed your application. It is now awaiting final approval from the superadmin.' },
};

export default function ApplicationStatus() {
  const { user, setUser } = useUser();
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState('');

  const status = user?.applicationStatus || 'PendingReview';
  const meta = STATUS_LABELS[status] || STATUS_LABELS.PendingReview;

  const refresh = async () => {
    setRefreshing(true); setErr('');
    try {
      const r = await axios.get('/User/MyStatus');
      const newStatus = r.data?.applicationStatus || 'Active';
      const mustAccept = r.data?.mustAcceptAgreement === true;
      localStorage.setItem('ApplicationStatus', newStatus);
      localStorage.setItem('MustAcceptAgreement', String(mustAccept));
      // Hard reload past Active state so the route guard sees the fresh user context
      // (not the stale PendingApproval in React state). This mirrors how the agreement
      // gate transitions to /dashboard after acceptance.
      if (newStatus === 'Active') {
        window.location.href = mustAccept ? '/accept-agreement' : '/dashboard';
        return;
      }
      setUser(prev => ({ ...prev, applicationStatus: newStatus, mustAcceptAgreement: mustAccept }));
    } catch (e) {
      setErr(typeof e?.response?.data === 'string' ? e.response.data : 'Failed to refresh status');
    } finally {
      setRefreshing(false);
    }
  };

  // Auto-refresh once on mount in case status changed since last login
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const logout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper sx={{ maxWidth: 520, width: '100%', p: { xs: 3, md: 5 }, borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <img src="/nexwaveLogo.png" alt="Nexwave" style={{ height: 48, marginBottom: 8 }} />
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>
            Hi {user?.firstName || user?.username}!
          </Typography>
          <Typography sx={{ fontSize: '0.85rem', color: '#64748B', fontFamily: PP }}>
            Application Status
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Chip label={meta.label} sx={{ backgroundColor: meta.bg, color: meta.color, fontWeight: 600, fontSize: '0.85rem', px: 1.5, py: 2, fontFamily: PP }} />
        </Box>

        <Typography sx={{ fontSize: '0.9rem', color: '#374151', fontFamily: PP, mb: 3, textAlign: 'center' }}>
          {meta.desc}
        </Typography>

        <Alert severity="info" sx={{ borderRadius: '8px', fontFamily: PP, mb: 3, fontSize: '0.8rem' }}>
          You'll be able to access the dashboard and start using the app once your application is fully approved. We'll send you a notification when something changes.
        </Alert>

        {err && <Alert severity="error" sx={{ borderRadius: '8px', fontFamily: PP, mb: 2 }}>{err}</Alert>}

        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={refresh}
            disabled={refreshing}
            startIcon={refreshing ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
            sx={{
              fontFamily: PP, fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none',
              background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
              '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)', boxShadow: 'none' },
            }}
          >
            {refreshing ? 'Refreshing…' : 'Refresh Status'}
          </Button>
          <Button
            onClick={logout}
            sx={{ fontFamily: PP, textTransform: 'none', color: '#64748B' }}
          >
            Log out
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
